import React, { Fragment, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Textarea from '../../styles/common/Textarea';
import AppButton from '../../styles/common/AppButton';
import AttachFile from '../Icons/AttachFile';
import Close from '../Icons/Close';
import { showErrorToast } from '../../lib/tools';
import Badge from '../Parts/Badge';
import { PAINT_COLORS } from '../../lib/constants';
import ColorSF2 from '../../styles/common/ColorSF2';

const Dialog = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;
const ChatTextarea = styled(Textarea)`
  width: 340px;
  height: 120px;
  padding: 10px;
`;
const Bottoms = styled.div`
  display: flex;
  flex-direction: column;
  font-size: 12px;
`;
const TextValidationMessage = styled.div`
  font-size: 12px;
  display: flex;
  align-items: flex-end;
  flex-direction: column;
`;
const FileInfo = styled.div`
  margin: 10px 0px;
  display: flex;
  justify-content: flex-start;
  flex-wrap: wrap;
  max-height: 100px;
  overflow-y: auto;
`;
const AttachFileName = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #fff;
  font-size: 12px;
`;
const FileName = styled.div`
  text-align: center;
  margin-right: 4px;
  color: ${props => props.color || ColorSF2.black};
`;
const FileDeleteButton = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
`;
const ActionsWrapper = styled.div`
  display: flex;
  margin-left: auto;
  svg {
    margin-right: 10px;
  }
`;
const AttachfileInput = styled.input`
  display: none;
`;
const CloseButton = styled(Close)`
  margin-left: auto;
  margin-bottom: 15px;
`;

const ChatHistoriesWrapper = styled.div`
  padding-bottom: 1px;
  display: flex;
  flex-direction: column;
  max-height: 340px;
  overflow-y: auto;
`;
const ChatHistoryWrapper = styled.div`
  margin: 10px;
  display: flex;
  flex-direction: column;
  background: rgba(0, 0, 0, 0.4);
  border-radius: 20px;
  padding: 10px;
`;
const ChatHistory = styled.div`
  display: flex;
  flex-wrap: wrap;
`;
const MemberNameWrapper = styled.div`
  display: flex;
`;
const MemberName = styled.span`
  margin-left: 4px;
  margin-right: 10px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-size: 12px;
  font-weight: 600;
  color: white;
`;
const ChatSendWrapper = styled.div`
  margin-top: 10px;
`;
const ChatText = styled.div`
  white-space: break-spaces;
  overflow-wrap: break-word;
  word-break: break-all;
  overflow: auto;
  color: ${ColorSF2.white};
  font-size: 12px;
`;

const ChatLogsEnd = styled.p`
  padding-bottom: 10px;
`;
const ChatFileInfo = styled(FileInfo)`
  margin: 4px 0px 0px;
`;
const MAX_CHAT_DATA_LENGTH = 200;
const MAX_ATTACH_FILE_SIZE = 1024 * 1024 * 1024 * 1024;
const FILE_UPLOAD_TARGET_TYPES = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/gif',
];

/**
 * チャットダイアログを表示するコンポーネント
 * @function
 * @param {Object} props - プロパティ
 * @param {Object} props.userInfo - ユーザー情報
 * @param {Array} props.members - 通話に参加しているメンバーのリスト
 * @param {boolean} props.sharerJoined - 共有相手がルーム参加しているかどうか
 * @param {Array} props.chatHistories - チャットの履歴のstate
 * @param {Function} props.setChatHistories - チャット履歴を親コンポーネントで管理するuseStateに設定する関数
 * @param {Function} props.send - チャットメッセージの送信時のコールバック関数
 * @param {Function} props.close - ダイアログを閉じる時のコールバック関数
 * @returns {ReactElement} チャットダイアログの要素
 */
const ChatDialog = ({
  userInfo,
  members,
  sharerJoined,
  chatHistories,
  setChatHistories,
  send,
  close,
}) => {
  const intl = useIntl();
  const { formatMessage } = intl;
  const [chatData, setChatData] = useState('');
  const [attachFiles, setAttachFiles] = useState([]);
  const [totalFileSize, setTotalFileSize] = useState(0);
  const enableSendButton = sharerJoined && (chatData || attachFiles.length > 0);
  const chatDataLengthMessage = formatMessage(
    { id: 'common.message.validation.lengthLeft' },
    { count: `${MAX_CHAT_DATA_LENGTH - chatData.length}` },
  );
  const edit = data => {
    if (sharerJoined) {
      setChatData(data);
    }
  };
  // ファイル追加処理
  const addFile = files => {
    const copyAttachFiles = attachFiles.slice();
    const errorFiles = [];
    Object.values(files).forEach(file => {
      // ファイルサイズチェック
      const fileSize = file.size;
      if (totalFileSize + fileSize > MAX_ATTACH_FILE_SIZE) {
        showErrorToast(
          formatMessage({ id: 'common.message.validation.totalFileSize' }),
        );
        return;
      }
      // ファイル拡張子チェック
      if (!FILE_UPLOAD_TARGET_TYPES.includes(file.type)) {
        errorFiles.push(file.name);
      } else {
        // ファイルサイズ、拡張子がOKの場合は追加処理
        copyAttachFiles.splice(copyAttachFiles.length, 0, file);
        setTotalFileSize(totalFileSize + fileSize);
      }
    });
    setAttachFiles(copyAttachFiles);
    if (errorFiles.length > 0) {
      showErrorToast(
        formatMessage(
          { id: 'common.message.validation.extension' },
          { file: errorFiles.join(', ') },
        ),
      );
    }
  };
  // ファイル削除処理
  const deleteFile = index => {
    const fileSize = attachFiles[index].size;
    const files = attachFiles.slice();
    files.splice(index, 1);
    setAttachFiles(files);
    setTotalFileSize(totalFileSize - fileSize);
  };
  const disabledAttachFile = totalFileSize >= MAX_ATTACH_FILE_SIZE;
  // ファイルの貼り付け処理
  const convertFileToImage = files => {
    if (typeof files !== 'object') {
      return;
    }
    addFile(files);
  };

  // チャット履歴(仮)
  const moveBottom = () => {
    const element = document.getElementById('EndChatHistories');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  };
  const addHistory = () => {
    const work = chatHistories.slice();
    const files = attachFiles.map(file => file.name);
    work.push({ text: chatData, files });
    setChatHistories(work);
    setChatData('');
    setAttachFiles([]);
    const obj = document.getElementById('Remote-chat-textarea');
    if (obj) {
      obj.value = '';
    }
    moveBottom();
  };
  const { index: colorIndex } = members.find(
    member => member.operatorId === userInfo.operatorId,
  ) || { index: -1 };
  const paintColor =
    (colorIndex > -1 &&
      PAINT_COLORS.length > colorIndex &&
      PAINT_COLORS[colorIndex]) ||
    PAINT_COLORS[0];
  return (
    <Dialog>
      <CloseButton color={ColorSF2.black} onClick={close} className="button" />
      <ChatHistoriesWrapper>
        {chatHistories.map((history, index) => {
          const key = `chat-history-${index}`;
          return (
            <Fragment key={key}>
              <ChatHistoryWrapper>
                <ChatHistory>
                  <MemberNameWrapper>
                    <Badge showAlways bgColor={paintColor} />
                    <MemberName>{userInfo.name}</MemberName>
                  </MemberNameWrapper>
                  <ChatText>{history.text}</ChatText>
                </ChatHistory>
                <ChatFileInfo>
                  {history.files.map((file, index2) => {
                    const fileKey = `chat-log-files-${index2}`;
                    return (
                      <AttachFileName key={fileKey}>
                        <AttachFile
                          color={ColorSF2.white}
                          width="16px"
                          height="16px"
                        />
                        <FileName color={ColorSF2.white}>{file}</FileName>
                      </AttachFileName>
                    );
                  })}
                </ChatFileInfo>
              </ChatHistoryWrapper>
            </Fragment>
          );
        })}
        <ChatLogsEnd id="EndChatHistories">&nbsp;</ChatLogsEnd>
      </ChatHistoriesWrapper>
      <ChatSendWrapper>
        <ChatTextarea
          id="Remote-chat-textarea"
          type="text"
          defaultValue={chatData}
          onChange={e => edit(e.target.value)}
          maxLength={MAX_CHAT_DATA_LENGTH}
          placeholder={formatMessage(
            { id: 'common.placeholder' },
            { name: formatMessage({ id: 'common.placeholder.item.sentence' }) },
          )}
          onPaste={e => convertFileToImage(e.clipboardData.files)}
        />
        <TextValidationMessage>
          {chatDataLengthMessage && <span>{chatDataLengthMessage}</span>}
        </TextValidationMessage>
        <Bottoms>
          <FileInfo>
            {attachFiles.map((file, index) => {
              const key = `remote-chat-file-name-${index}`;
              return (
                <AttachFileName key={key} className="button">
                  <AttachFile
                    color={ColorSF2.black}
                    width="16px"
                    height="16px"
                  />
                  <FileName>{file.name}</FileName>
                  <FileDeleteButton
                    id={`DeleteRemoteChatFile-${index}`}
                    role="presentation"
                    className="button"
                    onClick={() => deleteFile(index)}
                  >
                    <Close color={ColorSF2.black} width="12px" height="12px" />
                  </FileDeleteButton>
                </AttachFileName>
              );
            })}
          </FileInfo>
          <ActionsWrapper>
            <AppButton
              className={sharerJoined && !disabledAttachFile && 'button'}
              borderColor={ColorSF2.black}
              color={ColorSF2.black}
              bgColor={ColorSF2.skyBlue80}
              disabled={!sharerJoined || disabledAttachFile}
              width="80px"
              height="40px"
              onClick={() => {
                const inputElement = document.getElementById(
                  'Remote-Chatbox-File-Input',
                );
                if (inputElement) {
                  inputElement.value = '';
                  if (!disabledAttachFile) {
                    inputElement.click();
                  }
                }
              }}
            >
              <AttachFile color={ColorSF2.black} width="18px" height="18px" />
              <FormattedMessage id="common.label.attach" />
              <AttachfileInput
                id="Remote-Chatbox-File-Input"
                type="file"
                multiple
                accept=".pdf,image/jpeg,image/png,image/gif"
                onChange={e => addFile(e.target.files)}
              />
            </AppButton>
            <AppButton
              className={enableSendButton && 'button'}
              bgColor={ColorSF2.ceruleanBlue}
              disabled={!enableSendButton}
              width="80px"
              height="40px"
              onClick={() => {
                if (enableSendButton) {
                  addHistory();
                  send({ chatData, files: attachFiles });
                }
              }}
            >
              <FormattedMessage id="common.label.send" />
            </AppButton>
          </ActionsWrapper>
        </Bottoms>
      </ChatSendWrapper>
    </Dialog>
  );
};

ChatDialog.propTypes = {
  userInfo: PropTypes.object.isRequired,
  members: PropTypes.array.isRequired,
  sharerJoined: PropTypes.bool.isRequired,
  chatHistories: PropTypes.array.isRequired,
  setChatHistories: PropTypes.func.isRequired,
  send: PropTypes.func.isRequired,
  close: PropTypes.func.isRequired,
};

export default ChatDialog;
